import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { FileService } from '@app/core/files';
import { ToastrService } from 'ngx-toastr';
import { MessageService } from 'primeng/api';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

@Component({
  selector: 'afoone-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor {
  @Input() label: string = 'Subir archivo';
  @Input() maxSize: number = 2 * 1024 * 1024; // 2MB

  private onChange: (file: File | null) => void = () => {};
  private onTouched: () => void = () => {};

  public disabled: boolean = false;

  @Output() fileSelected = new EventEmitter<File>();
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  public filename: string = '';

  constructor(
    private fileService: FileService,
    private toastrService: ToastrService,
    private messageService: MessageService
  ) {}

  // ControlValueAccessor methods
  writeValue(value: File | null): void {
    if (value) {
      this.filename = value.name;
    } else {
      this.filename = '';
      this.fileUpload?.clear();
    }
  }

  registerOnChange(fn: (file: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelect(event: any) {
    const file: File = event.files?.[0];

    if (!ALLOWED_TYPES.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Tipo de archivo no permitido',
        detail: 'Solo se permiten imágenes, PDF y documentos de Office.',
      });
      this.fileUpload.clear(); // limpia el input
      return;
    }

    if (file && file.size > this.maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error al subir el archivo',
        detail: `El archivo no puede ser mayor a ${
          this.maxSize / 1024 / 1024
        } MB.`,
      });
    } else {
      this.filename = file.name;
      this.fileSelected.emit(file);
    }
    this.onChange(file);
    this.onTouched();
  }

  clearFile() {
    this.fileUpload.clear();
    this.filename = '';
    this.onChange(null);
    this.toastrService.info('Archivo eliminado');
  }
}
